import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, MapPin, CheckCircle2, Ban, Gift, Pencil, Trash2, MessageSquare, Bell } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription } from "@/components/ui/alert-dialog";

const fmtD = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const mapsUrl = (g) => (g?.lat != null ? `https://www.google.com/maps?q=${g.lat},${g.lng}` : null);

const EditDialog = ({ u, onClose, onSaved, isSU }) => {
  const [f, setF] = useState({ name: u.name, email: u.email, phone: u.phone || "", password: "", plan_type: u.plan?.type || "trial", plan_expires_at: u.plan?.expires_at?.slice(0, 10) || "", role: u.role });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, password: f.password || null, plan_expires_at: f.plan_expires_at ? `${f.plan_expires_at}T23:59:00+00:00` : null, role: isSU && u.role !== "superadmin" ? f.role : null };
    try { const { data } = await api.patch(`/admin/users/${u.id}`, body); onSaved(data); toast.success("Utente aggiornato"); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "w-full bg-[#050b18] border border-cyan-500/25 rounded-lg px-3 py-2 text-sm outline-none";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0a1224] border-cyan-500/30" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Modifica utente</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-xs text-slate-400">Nome<input className={inp} value={f.name} onChange={set("name")} data-testid="edit-name-input" /></label>
          <label className="text-xs text-slate-400">Email<input className={inp} value={f.email} onChange={set("email")} data-testid="edit-email-input" /></label>
          <label className="text-xs text-slate-400">Telefono<input className={inp} value={f.phone} onChange={set("phone")} data-testid="edit-phone-input" /></label>
          <label className="col-span-2 text-xs text-slate-400">Nuova password (opzionale)<input className={inp} type="password" value={f.password} onChange={set("password")} data-testid="edit-password-input" /></label>
          <label className="text-xs text-slate-400">Piano
            <select className={inp} value={f.plan_type} onChange={set("plan_type")} data-testid="edit-plan-select"><option value="trial">Prova</option><option value="pro">Pro</option><option value="none">Nessuno</option></select>
          </label>
          <label className="text-xs text-slate-400">Scadenza<input type="date" className={inp} value={f.plan_expires_at} onChange={set("plan_expires_at")} data-testid="edit-expiry-input" /></label>
          {isSU && u.role !== "superadmin" && (
            <label className="col-span-2 text-xs text-slate-400">Ruolo
              <select className={inp} value={f.role} onChange={set("role")} data-testid="edit-role-select"><option value="user">Utente</option><option value="admin">Admin</option></select>
            </label>
          )}
        </div>
        <button onClick={save} className="btn-aurora rounded-xl py-2.5 text-sm uppercase mt-2" data-testid="edit-save-btn">Salva modifiche</button>
      </DialogContent>
    </Dialog>
  );
};

const ROLE_CLS = { superadmin: "border-amber-400/60 text-amber-300", admin: "border-violet-400/60 text-violet-300" };
const IB = "p-1.5 rounded-md hover:bg-white/10 transition-colors";

const PhoneCell = ({ u }) => (
  <td className="px-3 py-3 font-mono text-xs" data-testid={`user-phone-${u.id}`}>
    {u.phone ? (
      <div className="flex flex-col gap-0.5">
        <a href={`tel:${u.phone.replace(/\s/g, "")}`} className="text-white hover:text-cyan-300">{u.phone}</a>
        <a href={`https://wa.me/${u.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:text-amber-300 text-[11px]" data-testid={`user-whatsapp-${u.id}`}>WhatsApp</a>
      </div>
    ) : <span className="text-slate-500">non fornito</span>}
  </td>
);

const RoleCell = ({ u }) => (
  <td className="px-3 py-3">
    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border ${ROLE_CLS[u.role] || "border-slate-600 text-slate-400"}`}>{u.role}</span>
    <p className={`text-xs mt-1 ${u.status === "attivo" ? "text-emerald-300" : "text-rose-400"}`} data-testid={`user-status-${u.id}`}>{u.status}</p>
  </td>
);

const GeoCell = ({ u }) => {
  const g = u.geo || {};
  const link = mapsUrl(g);
  return (
    <td className="px-3 py-3 text-xs">
      <p>{[g.city, g.country].filter(Boolean).join(", ") || "—"}</p>
      {link && <a href={link} target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-amber-300 inline-flex items-center gap-1 mt-0.5" data-testid={`admin-geo-map-link-${u.id}`}><MapPin size={12} /> Google Maps {g.source === "browser" ? "(GPS)" : "(IP)"}</a>}
    </td>
  );
};

const StatusButton = ({ u, act }) => (u.status === "attivo"
  ? <button title="Disabilita" className={`${IB} text-rose-300`} disabled={u.role === "superadmin"} onClick={() => act(() => api.post(`/admin/users/${u.id}/status/disabilitato`), "Utente disabilitato")} data-testid={`btn-disable-${u.id}`}><Ban size={15} /></button>
  : <button title="Attiva" className={`${IB} text-emerald-300`} onClick={() => act(() => api.post(`/admin/users/${u.id}/status/attivo`), "Utente attivato")} data-testid={`btn-activate-${u.id}`}><CheckCircle2 size={15} /></button>);

const UserActions = ({ u, isSU, act, onEdit, onMessage, onPush, onDelete }) => (
  <td className="px-3 py-3">
    <div className="flex items-center gap-0.5">
      <StatusButton u={u} act={act} />
      <button title="Concedi 30 giorni" className={`${IB} text-amber-300`} onClick={() => act(() => api.post(`/admin/users/${u.id}/grant30`), "Concessi 30 giorni")} data-testid={`btn-grant30-${u.id}`}><Gift size={15} /></button>
      <button title="Modifica" className={`${IB} text-cyan-300`} onClick={() => onEdit(u)} data-testid={`btn-edit-${u.id}`}><Pencil size={15} /></button>
      <button title="Messaggio" className={`${IB} text-violet-300`} onClick={() => onMessage(u)} data-testid={`btn-message-${u.id}`}><MessageSquare size={15} /></button>
      <button title="Push" className={`${IB} text-sky-300`} onClick={() => onPush(u)} data-testid={`admin-send-push-btn-${u.id}`}><Bell size={15} /></button>
      {isSU && u.role !== "superadmin" && <button title="Elimina" className={`${IB} text-rose-400`} onClick={() => onDelete(u)} data-testid={`btn-delete-${u.id}`}><Trash2 size={15} /></button>}
    </div>
  </td>
);

const termsLabel = (u) => (u.terms_accepted_at ? `T&C accettati ${new Date(u.terms_accepted_at).toLocaleDateString("it-IT")}` : "T&C: non registrati");

const UserRow = ({ u, ...actions }) => (
  <tr className="border-b border-slate-800/70 hover:bg-cyan-500/5" data-testid={`admin-user-row-${u.id}`}>
    <td className="px-3 py-3"><p className="text-white font-medium">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p>
      <p className="text-[10px] font-mono mt-0.5 text-slate-500" data-testid={`user-terms-${u.id}`}>{termsLabel(u)}</p></td>
    <PhoneCell u={u} />
    <RoleCell u={u} />
    <td className="px-3 py-3 text-xs"><p className="uppercase font-mono">{u.plan?.type}</p><p className={u.subscription_active ? "text-emerald-300" : "text-rose-400"} data-testid={`user-expiry-${u.id}`}>{fmtD(u.plan?.expires_at)}</p></td>
    <td className="px-3 py-3 font-mono text-xs">{u.last_ip || "—"}</td>
    <td className="px-3 py-3 text-xs max-w-[160px] truncate" title={u.user_agent}>{u.os || "—"}</td>
    <GeoCell u={u} />
    <td className="px-3 py-3 font-mono text-xs text-cyan-300">{u.usage_minutes || 0}</td>
    <UserActions u={u} {...actions} />
  </tr>
);

export const AdminUsers = ({ me, onMessage, onPush }) => {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const load = (qq = q) => api.get("/admin/users", { params: { q: qq || undefined } }).then((r) => setUsers(r.data));
  useEffect(() => { load(""); }, []); // eslint-disable-line
  const replace = (u) => setUsers((x) => x.map((y) => (y.id === u.id ? u : y)));
  const act = async (fn, msg) => { try { const { data } = await fn(); replace(data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); } };
  const isSU = me.role === "superadmin";

  const doDelete = async () => {
    try { await api.delete(`/admin/users/${del.id}`); setUsers((x) => x.filter((y) => y.id !== del.id)); toast.success("Utente eliminato"); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div data-testid="admin-users">
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 mb-4 max-w-md">
        <div className="flex-1 flex items-center gap-2 bg-[#050b18]/80 border border-cyan-500/25 rounded-xl px-3">
          <Search size={15} className="text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, email, telefono" className="flex-1 bg-transparent py-2.5 text-sm outline-none" data-testid="admin-users-search" />
        </div>
        <button className="btn-ghost-hud rounded-xl px-4 text-sm" data-testid="admin-users-search-btn">Cerca</button>
      </form>
      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#070d1b]/85">
        <table className="w-full text-left text-sm text-slate-300 min-w-[1100px]" data-testid="admin-users-table">
          <thead className="text-[11px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-800">
            <tr>{["Utente", "Telefono", "Ruolo / Stato", "Piano", "IP", "Sistema", "Posizione", "Minuti", "Azioni"].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map((u) => <UserRow key={u.id} u={u} isSU={isSU} act={act} onEdit={setEdit} onMessage={onMessage} onPush={onPush} onDelete={setDel} />)}
          </tbody>
        </table>
      </div>
      {edit && <EditDialog u={edit} isSU={isSU} onClose={() => setEdit(null)} onSaved={replace} />}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-[#0a1224] border-rose-500/30">
          <AlertDialogHeader><AlertDialogTitle>Eliminare {del?.name}?</AlertDialogTitle><AlertDialogDescription>Account e dati collegati verranno cancellati definitivamente.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600" data-testid="admin-delete-confirm">Elimina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
